import {
    ImageResourceItem,
    Inject,
    ISimpleControllerConfiguration,
    ISpriteEvent, ModelEvents,
    SimpleBaseController,
    SoundType,
} from '@denishussar/html5-game-engine';
import * as PIXI from 'pixi.js';
import { Container, Graphics, loader, Point, Sprite, Texture } from 'pixi.js';
import { checkMobile } from '../../Common/Utils';
import { SoundConstants } from '../../Constants/Sounds';
import { GameLogic } from '../../Logic/GameLogic';
import { BoosterTypeEnum, GameModel, InstrumentTypeEnum } from '../../Models/GameModel';
import { SpriteAnimation } from './SpriteAnimation';
import { GameCanvasRenderer } from '../../Utils/GameCanvasRednerer';
import { BombBooster } from '../../../game/Logic/Boosters/Bomb';
import { ThreadBooster } from '../../../game/Logic/Boosters/Thread';
import {BucketBooster} from "../../Logic/Boosters/Bucket";


interface IConfiguration {

}

export interface IGamePictureControllerOption {
    fillColorsAfterInitializtion: boolean,
    interactiveGlobalRectangle: PIXI.Rectangle,

}

class PicturesTexture {

    private static __frameCount = 9;
    private static __baseTexture: PIXI.BaseTexture | undefined;

    public static getTextures() {
        const baseTexture = PicturesTexture.getBaseTexture();
        const frameCount = PicturesTexture.__frameCount;
        const frameWidth = baseTexture.source.width / frameCount;
        const frameHeight = baseTexture.source.height;

        const result: Texture[] = [];
        for (let i = 0; i < frameCount; i++) {
            result.push(new PIXI.Texture(baseTexture, new PIXI.Rectangle(i * frameWidth, 0, frameWidth, frameHeight)));
        }

        return result;
    }

    private static getBaseTexture() {
        if (!PicturesTexture.__baseTexture) {
            PicturesTexture.__baseTexture = PicturesTexture.setupTexture();
        }
        return PicturesTexture.__baseTexture;
    }

    // todo: переделать на рендер таргет
    private static setupTexture() {
        const frames = PicturesTexture.__frameCount;
        const textures: Texture[] = [];

        for (let i = 1; i <= frames; i++) {
            textures.push(loader.resources[`assets/pixel_0${i}.png`].texture);
        }

        const canvas = document.createElement('canvas');
        canvas.width = textures[0].baseTexture.source.width * frames;
        canvas.height = textures[0].baseTexture.source.height;
        const ctx = canvas.getContext('2d');

        for (let i = 0; i < textures.length; i++) {
            ctx.drawImage(textures[i].baseTexture.source, i * textures[0].baseTexture.source.width, 0);
        }

        return new PIXI.BaseTexture(canvas);
    }
}

class SpriteAnimationPool {
    private pool: SpriteAnimation[] = [];

    constructor(poolCount: number) {
        for (let i = 0; i < poolCount; i++) {
            this.pool.push(new SpriteAnimation(PicturesTexture.getTextures()));
        }
    }

    public take() {
        return this.pool.length > 0 ? this.pool.pop() : new SpriteAnimation(PicturesTexture.getTextures());
    }

    public release(sprite: SpriteAnimation) {
        sprite.reset();
        this.pool.push(sprite);
    }
}

class BrushPool {
    private count = 0;
    private pool: Sprite[] = [];

    constructor(radius: number) {
        for (let i = -radius; i < radius; i++) {
            const height = Math.floor(Math.sqrt(radius * radius - i * i));
            for (let j = -height; j < height; j++) {
                this.count++;
            }
        }
    }

    public update() {
        const length = this.pool.length > this.count ? this.pool.length : this.pool.length + 10;
        for (let i = this.pool.length; i < length; i++) {
            this.pool.push(new Sprite(Texture.from('assets/pixel.png')))
        }
    }

    public take() {
        return this.pool.length > 0 ? this.pool.pop() : new Sprite(Texture.from('assets/pixel.png'));
    }
}

export class GamePictureController extends SimpleBaseController {
    $model: GameModel;

    private pictureTextureWidth = 1;
    private pictureTextureHeight = 1;

    private gameGanvasRenderer: GameCanvasRenderer;

    private pixelMap: Sprite[][] = [];

    private gridTexture: Texture;
    private pixelAnimationContainer: Container;
    private pixelBrushPool: BrushPool;
    private pixelAnimationPool: SpriteAnimationPool;
    private pixelAnimationList: SpriteAnimation[] = [];
    private pixelAnimationMap: SpriteAnimation[][] = [];

    private initialized = false;

    public constructor(
        @Inject private options: IGamePictureControllerOption,
        @Inject private gameLogic: GameLogic,
        @Inject private modelEvents: ModelEvents,
        @Inject private model: GameModel,
    ) {
        super('GamePictureController');
    }

    protected getConfiguration(): ISimpleControllerConfiguration {
        const resources = {
            'spinner': new ImageResourceItem('spinner', 'spinner.png'),
            'grid': new ImageResourceItem('grid', 'grid.png')
        };
        return <ISimpleControllerConfiguration>{
            ...(
                checkMobile() ? this.desktopConfiguration() : this.desktopConfiguration()
            ),
            resources,
            sounds: [
                {
                    id: SoundConstants.BOOSTER_BRUSH,
                    type: SoundType.Music,
                },
                {
                    id: SoundConstants.BOOSTER_NEEDLE,
                    type: SoundType.Music,
                },
                {
                    id: SoundConstants.BOOSTER_BUCKET,
                    type: SoundType.Music,
                },
            ],
            modelWatch: {},
            modelEvents: {
                onTextureCreated: () => {
                    this.init();
                },
                doneColor: () => {
                    this.gameLogic.setPaintEnabled(false);
                },
                setColor: (color: number, index: number) => {
                    this.checkGrid();
                    this.gameGanvasRenderer.showColor(index);
                },
                setBoosterType: (value: BoosterTypeEnum) => {
                    this.checkGrid();

                    if (this.$model.activeBooster) {
                        this.display.removeChild(this.$model.activeBooster.sprite);
                        this.$model.activeBooster = undefined;
                    }

                    switch (value) {
                        case BoosterTypeEnum.yarn:
                            this.$model.activeBooster = new BombBooster(
                                this.$model,
                                this.soundManager,
                                this.gameLogic,
                                this.modelEvents,
                                this.renderer.renderer
                            );
                            this.display.addChild(this.$model.activeBooster.sprite);
                            break;

                        case BoosterTypeEnum.needle:
                            this.$model.activeBooster = new ThreadBooster(
                                this.$model,
                                this.soundManager,
                                this.gameLogic,
                                this.modelEvents,
                                this.renderer.renderer
                            );
                            this.display.addChild(this.$model.activeBooster.sprite);
                            break;

                        case BoosterTypeEnum.bucket:
                            this.$model.activeBooster = new BucketBooster(
                                this.$model,
                                this.soundManager,
                                this.gameLogic,
                                this.modelEvents,
                                this.renderer.renderer
                            );
                            this.display.addChild(this.$model.activeBooster.sprite);
                            break;
                    }
                },
                setInstrumentType: (value: InstrumentTypeEnum) => {
                    this.checkGrid();
                    switch (value) {
                        case InstrumentTypeEnum.booster:
                            break;
                        default:
                            if (this.$model.activeBooster) {
                                this.display.removeChild(this.$model.activeBooster.sprite);
                                this.$model.activeBooster = undefined;
                            }
                    }
                },
                fillPixelWithAnimation: (x: number, y: number, color: number) => {
                    if (!this.pixelAnimationMap) {
                        this.pixelAnimationMap = [];
                    }
                    if (!this.pixelAnimationMap[x]) {
                        this.pixelAnimationMap[x] = [];
                    }

                    const animation = this.pixelAnimationPool.take();

                    animation.x = x * GameModel.pixelSize;
                    animation.y = y * GameModel.pixelSize;
                    animation.tint = color;
                    animation.loop = false;

                    this.pixelAnimationList.push(animation);
                    this.pixelAnimationMap[x][y] = animation;

                    this.pixelAnimationContainer.addChild(animation);

                    animation.once('complete', (self) => {
                        this.gameLogic.fillPixel(x, y, color);
                        this.pixelAnimationContainer.removeChild(self);
                        this.pixelAnimationPool.release(self);
                        this.pixelAnimationList.splice(this.pixelAnimationList.indexOf(self), 1);
                        this.pixelAnimationMap[x][y] = null;

                    });

                    animation.gotoAndPlay(0);
                },
                fillPixel: (x: number, y: number, color: number) => {
                    this.fillPixelExactly(x, y, color);
                }
            },
        };
    }

    private fillPixelExactly(x: number, y: number, color: number) {
        if (!this.pixelMap) {
            this.pixelMap = [];
        }
        if (!this.pixelMap[x]) {
            this.pixelMap[x] = [];
        }
        if (this.pixelMap[x][y]) {
            return;
        }

        const sprite = this.pixelBrushPool.take();//new Sprite(Texture.from('assets/pixel.png')); // TODO: preload
        sprite.tint = color;
        sprite.interactive = false;
        this.pixelMap[x][y] = sprite;
        this.gameGanvasRenderer?.drawVoxel(sprite, x, y);
    }

    private checkGrid(): void {
        const instrument = this.gameLogic.getInstrumentType();

        switch (instrument) {
            case InstrumentTypeEnum.preview:
                this.gameGanvasRenderer?.setGridVisibility(false);
                break;
            default:
                this.gameGanvasRenderer?.setGridVisibility(true);
        }
    }

    protected desktopConfiguration(): IConfiguration {
        this.display.on('touchend', (e: PIXI.interaction.InteractionEvent) => {
            if (!this.gameGanvasRenderer) return;

            const position = this.getPixelPositionByMouse(e.data.global);
            if (position) {
                switch (this.gameLogic.getInstrumentType()) {
                    case InstrumentTypeEnum.paint:
                        if (this.canFillByPointer(e.data.global)) {
                            this.fillPixelWithAnimation(position.x, position.y);
                            this.gameLogic.setPaintEnabled(false);
                        }
                        break;
                    case InstrumentTypeEnum.booster:
                        this.$model.activeBooster.fill(position.x, position.y);
                        break;
                }
            }
        });

        return {
            onPointerMove: (e: ISpriteEvent) => {
                if (!this.gameGanvasRenderer) return;
                // todo: добавить проверку на нажатую кнопку, чтобы не делать дорогую операцию перемножения матриц
                const position = this.getPixelPositionByMouse(e.globalPosition);

                if (position) {
                    switch (this.gameLogic.getInstrumentType()) {
                        case InstrumentTypeEnum.booster: {
                            this.$model.activeBooster?.movePointer(position.x, position.y);
                        }

                        case InstrumentTypeEnum.paint: {
                            if (this.gameLogic.isPaintEnabled() && this.canFillByPointer(e.globalPosition)) {
                                this.fillPixelWithAnimation(position.x, position.y);
                            }
                            break;
                        }
                    }
                }
            },

            // onPointerUp: (e: ISpriteEvent) => {
            //     console.log("UP")
            //     if (!this.gameGanvasRenderer) return;
            //
            //     const position = this.getPixelPositionByMouse(e.globalPosition);
            //     if (position) {
            //         switch (this.gameLogic.getInstrumentType()) {
            //             case InstrumentTypeEnum.paint:
            //                 if (this.gameLogic.isPaintEnabled() && this.canFillByPointer(e.globalPosition)) {
            //                     this.fillPixelWithAnimation(position.x, position.y);
            //                 }
            //                 break;
            //             case InstrumentTypeEnum.booster:
            //                 this.$model.activeBooster?.fill(position.x, position.y);
            //                 console.log("FILLED")
            //                 break;
            //         }
            //     }
            // }
        };
    }

    protected mobileConfiguration(): IConfiguration { //todo: какой-то треш тут
        // TODO неочевидно...

        let touched = [];
        let drawing = false;

        this.display.on('touchstart', (e: PIXI.interaction.InteractionEvent) => {
            if (!this.gameGanvasRenderer) return;
            touched.push(e);

            drawing = true;
            touched.forEach(() => {
                if ((e.data.originalEvent as TouchEvent).touches.length > 1) {
                    drawing = false;
                }
            });
            this.gameLogic.setPaintEnabled(drawing);
        });

        this.display.on('touchcancel', (e: PIXI.interaction.InteractionEvent) => {
            if (!this.gameGanvasRenderer) return;
            drawing = false;

            if (touched.length) {
                touched.shift();
            }
        });

        this.display.on('touchend', (e: PIXI.interaction.InteractionEvent) => {
            console.log("TOUCH_END")
            if (!this.gameGanvasRenderer) return;
            if (touched.length) {
                touched.shift();
            }
            if (!drawing) {
                return;
            }

            const position = this.getPixelPositionByMouse(e.data.global);
            if (position) {
                switch (this.gameLogic.getInstrumentType()) {
                    case InstrumentTypeEnum.paint:
                        if (this.canFillByPointer(e.data.global)) {
                            this.fillPixelWithAnimation(position.x, position.y);
                            this.gameLogic.setPaintEnabled(false);
                        }
                        break;
                    case InstrumentTypeEnum.booster:
                        this.$model.activeBooster.fill(position.x, position.y);
                        console.log("FILLED")
                        break;
                }
            }
        });

        this.display.on('touchmove', (e: PIXI.interaction.InteractionEvent) => {
            if (!this.gameGanvasRenderer) return;
            if (!drawing) {
                return;
            }

            const position = this.getPixelPositionByMouse(e.data.global);
            if (position) {
                switch (this.gameLogic.getInstrumentType()) {
                    case InstrumentTypeEnum.booster: {
                        console.log("TOUCH_MOVE")
                        this.$model.activeBooster?.movePointer(position.x, position.y);
                    }

                    case InstrumentTypeEnum.paint: {
                        if (this.gameLogic.isPaintEnabled() && this.canFillByPointer(e.data.global)) {
                            this.fillPixelWithAnimation(position.x, position.y);
                        }
                        break;
                    }
                }

            }
        });

        return {
            onPointerDown: (e: ISpriteEvent) => {

            },
            onPointerUp: (e: ISpriteEvent) => {

            },
            onPointerUpOutside: (e: ISpriteEvent) => {

            },
            onPointerMove: (e: ISpriteEvent) => {

            },
        };
    }

    protected onInitialize(): void {
        this.pixelAnimationContainer = new Container();
        this.gridTexture = this.gridTexture = this.$resources.get('grid').getTexture();

        if (this.$model.saveTexture) {
            this.init();
        }
        // const spector = new SPECTOR.Spector();
        // spector.displayUI();
        // (window as any).s = spector;
    }

    protected onResize(width: number, height: number, scale: number) {
    }

    protected onUpdate(frameRate: number) {
        this.pixelAnimationList.forEach(animation => {
            animation.update(frameRate)
        });

        this.pixelBrushPool.update();

    }

    private init(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        this.pictureTextureWidth = this.$model.saveTexture.baseTexture.width;
        this.pictureTextureHeight = this.$model.saveTexture.baseTexture.width;


        this.pixelAnimationPool = new SpriteAnimationPool(10);
        this.pixelBrushPool = new BrushPool(this.gameLogic.getBoosterBallThreadSize());

        this.gameGanvasRenderer = new GameCanvasRenderer({
            logic: this.gameLogic,
            context: this.renderer.renderer,
            image: this.$model.saveTexture,
            gridImage: this.gridTexture,
            pixelSize: GameModel.pixelSize,
        });
        this.gameGanvasRenderer.inject(this.display);
        this.display.addChild(this.pixelAnimationContainer);

        this.checkGrid();
        if (this.options.fillColorsAfterInitializtion) {
            setTimeout(() => this.fillAll(), 0);
        }

        if (this.display.mask) {
            this.display.removeChild(this.display.mask);
            this.display.mask.destroy();
        }

        this.display.mask = new Graphics(true);
        this.display.mask.beginFill(0xffffff);
        this.display.mask.drawRect(0, 0, this.getPictureWidth() * GameModel.pixelSize, this.getPictureHeight() * GameModel.pixelSize);
        this.display.mask.endFill();
        this.display.addChild(this.display.mask);
    }


    public click(e: ISpriteEvent): void {
        if (e.mouseEvent.button !== 0) {
            return;
        }
        const position = this.getPixelPositionByMouse(e.globalPosition);
        if (position) {
            switch (this.gameLogic.getInstrumentType()) {
                case InstrumentTypeEnum.paint:
                    if (this.canFillByPointer(e.globalPosition)) {
                        this.fillPixelWithAnimation(position.x, position.y);

                    }
                    break;
                case InstrumentTypeEnum.booster:
                    this.$model.activeBooster.fill(position.x, position.y);
                    break;
            }
        }
    }

    public touchEnd(e: PIXI.interaction.InteractionEvent): void {
        if (!this.gameGanvasRenderer) return;
        const position = this.getPixelPositionByMouse(e.data.global);
        if (position) {
            switch (this.gameLogic.getInstrumentType()) {
                case InstrumentTypeEnum.paint:
                    if (this.canFillByPointer(e.data.global)) {
                        this.fillPixelWithAnimation(position.x, position.y);
                        this.gameLogic.setPaintEnabled(false);
                    }
                    break;
                case InstrumentTypeEnum.booster:
                    this.$model.activeBooster.fill(position.x, position.y);
                    console.log("FILLED")
                    break;
            }
        }
    }

    private getPixelPositionByMouse(position: Point): Point | undefined {
        const point = this.display.toLocal(position, this.renderer.stage);
        // todo: use >> to devide by power of 2
        point.x /= GameModel.pixelSize;
        point.y /= GameModel.pixelSize;
        const min = 0;
        const max = this.getPictureWidth();

        if (point.x < min || point.x > max || point.y < min || point.y > max) {
            return undefined;
        }

        const x = Math.max(min, Math.min(Math.floor(point.x), max - 1));
        const y = Math.max(min, Math.min(Math.floor(point.y), max - 1));

        return new Point(x, y);
    }

    private getPictureWidth(): number {
        return this.pictureTextureWidth;
    }

    private getPictureHeight(): number {
        return this.pictureTextureHeight;
    }


    private fillPixelWithAnimation(x: number, y: number): void {
        if (!this.gameLogic.checkPixelInCanvas(x, y)) {
            return;
        }

        if (this.pixelMap && this.pixelMap[x]) {
            if (this.pixelMap[x][y]) {
                return;
            }
        }
        if (this.pixelAnimationMap && this.pixelAnimationMap[x]) {
            if (this.pixelAnimationMap[x][y]) {
                return;
            }
        }

        const pixelColor = this.gameLogic.getPixelColor(x, y);
        const currentColor = this.gameLogic.getColor();

        if (pixelColor === -1 || currentColor !== pixelColor) {
            return;
        }

        this.gameLogic.fillPixelWithAnimation(x, y, pixelColor);
    }

    public fillPixel(x: number, y: number, anyColor: boolean = false): void {
        if (!this.gameLogic.checkPixelInCanvas(x, y)) {
            return;
        }

        const pixelColor = this.gameLogic.getPixelColor(x, y);

        if (pixelColor === -1) {
            return;
        }

        const currentColor = anyColor ? pixelColor : this.gameLogic.getColor();

        if (currentColor === pixelColor) {
            this.gameLogic.fillPixel(x, y, currentColor);
        }
    }

    private fillAll(): void {
        for (let x = 0; x < this.getPictureWidth(); x++) {
            for (let y = 0; y < this.getPictureHeight(); y++) {
                const pixelColor = this.gameLogic.getPixelColor(x, y);
                if (this.gameLogic.isColored(x, y)) {
                    // todo: проверить из-за чего не работает синхронная отрисовка
                    // возможно, накладываются вызовы к асинхронному api webgl
                    // при подготовке буферов
                    setTimeout(() => this.fillPixelExactly(x, y, pixelColor), 20);
                }
            }
        }
    }

    private canFillByPointer(globalPosition: Point) {
        return this.options.interactiveGlobalRectangle && this.options.interactiveGlobalRectangle.contains(globalPosition.x, globalPosition.y);
    }
}
